import * as React from 'react';
import { createContext, useContext } from 'react';

import type { ApolloError } from '@apollo/client';
import type { ApolloQueryResult } from 'apollo-client';

import type { EnterpriseAuthoringPartner } from 'bundles/enterprise-admin-custom-courses/utils/privateAuthoringUtils';
import type { ThirdPartyOrganizationAuthoringPartnerQuery as Query } from 'bundles/enterprise-admin-home/queries/__generated__/ThirdPartyOrganizationAuthoringPartner';
import { useThirdPartyOrgAuthoringPartnerQuery } from 'bundles/enterprise-admin-home/queries/useThirdPartyOrgAuthoringPartnerQuery';

type PrivateAuthoringPartnerContextType = {
  enterpriseAuthoringPartners: (EnterpriseAuthoringPartner | null)[] | undefined;
  loading: boolean;
  error?: ApolloError;
  refetch?: () => Promise<ApolloQueryResult<Query>>;
};

type PropsFromCaller = {
  thirdPartyOrganizationId: string;
  children: React.ReactNode;
};

export const PrivateAuthoringPartnerContext = createContext<PrivateAuthoringPartnerContextType>({
  enterpriseAuthoringPartners: undefined,
  loading: false,
  error: undefined,
});

const PrivateAuthoringPartner = ({ children, thirdPartyOrganizationId }: PropsFromCaller) => {
  const { authoringPartners, loading, error, refetch } =
    useThirdPartyOrgAuthoringPartnerQuery(thirdPartyOrganizationId);

  return (
    <PrivateAuthoringPartnerContext.Provider
      value={{ enterpriseAuthoringPartners: authoringPartners, loading, error, refetch }}
    >
      {children}
    </PrivateAuthoringPartnerContext.Provider>
  );
};

export const usePrivateAuthoringPartner = () => useContext(PrivateAuthoringPartnerContext);

export default PrivateAuthoringPartner;
